import type { CohortRow, CohortScope } from '../types/dashboard';
import { PROGRAM_RETENTION_ADJ, SPORT_RETENTION_ADJ } from '../data/dimensions';
import { COHORT_INDEX, COHORTS_BY_SEASON, cohortKey } from './factData';
import { COHORT_SEASONS } from './calendar';
import { comboProgram, comboSport } from './combos';
import { aopById, territoryById } from './territory';
import { clamp, noise } from './random';

export interface RetentionAgg {
  base: number;
  retained: number;
  regRows: number;
  rate: number;
}

export interface RetentionPoint {
  base: number;
  comp: number;
  label: string;
  scope: number | null;
  network: number | null;
}

const GAP_ADJ: Record<number, number> = { 1: 0, 2: -0.04, 3: -0.07, 4: -0.02 };

function retentionRate(row: CohortRow, comp: number, within12: boolean): number {
  const t = territoryById.get(row.t);
  const entry = aopById.get(row.a);
  const gap = comp - row.s;
  let r =
    0.4 +
    (t?.quality ?? 0) * 0.07 +
    (entry?.aop.qualityAdj ?? 0) * 1.2 +
    SPORT_RETENTION_ADJ[comboSport(row.k)] +
    PROGRAM_RETENTION_ADJ[comboProgram(row.k)] +
    (GAP_ADJ[gap] ?? -0.09) +
    noise(`${row.a}|${row.k}|${row.s}|${comp}|ret`, 0.04);
  if (COHORT_SEASONS[comp]?.inProgress && !within12) r -= 0.05;
  if (within12) r += 0.12;
  return clamp(r, 0.05, 0.85);
}

/** Unique participants from the base cohort row who appear in the comparison period. */
function retainedCount(row: CohortRow, comp: number, within12: boolean): number {
  const retained = Math.round(row.participants * retentionRate(row, comp, within12));
  if (within12) return Math.min(retained, row.participants);
  const compRow = COHORT_INDEX.get(cohortKey(row.a, row.k, comp));
  return compRow ? Math.min(retained, compRow.participants) : 0;
}

function inScope(r: CohortRow, scope: CohortScope): boolean {
  return scope.territoryIds.has(r.t) && (!scope.aopIds || scope.aopIds.has(r.a)) && scope.comboMask[r.k];
}

const EMPTY: RetentionAgg = { base: 0, retained: 0, regRows: 0, rate: 0 };

export function aggregateRetention(
  scope: CohortScope,
  base: number,
  comp: number,
  within12: boolean,
  keyFn: (r: CohortRow) => string = () => 'all'
): Map<string, RetentionAgg> {
  const out = new Map<string, RetentionAgg>();
  const rows = COHORTS_BY_SEASON[base] ?? [];
  for (const r of rows) {
    if (!inScope(r, scope)) continue;
    const key = keyFn(r);
    const agg = out.get(key) ?? { ...EMPTY };
    agg.base += r.participants;
    agg.retained += retainedCount(r, comp, within12);
    agg.regRows += r.regRows;
    out.set(key, agg);
  }
  out.forEach((a) => {
    a.rate = a.base ? a.retained / a.base : 0;
  });
  return out;
}

export function retentionTotal(scope: CohortScope, base: number, comp: number, within12: boolean): RetentionAgg {
  if (base < 0 || comp >= COHORT_SEASONS.length) return { ...EMPTY };
  return aggregateRetention(scope, base, comp, within12).get('all') ?? { ...EMPTY };
}

export interface RetentionSummary {
  rate: number | null;
  prior: number | null;
  network: number | null;
  label: string;
}

/** Headline cohorts: Summer 2026 → Fall 2026 (season) and Fall 2025 → next 12 months (YOY). */
export const SEASON_KPI_BASE = 9;
export const YOY_KPI_BASE = 6;

export function retentionSummaries(scope: CohortScope, network: CohortScope): { season: RetentionSummary; yoy: RetentionSummary } {
  const summarize = (base: number, gap: number, within12: boolean, label: string): RetentionSummary => {
    const cur = retentionTotal(scope, base, base + gap, within12);
    const prior = retentionTotal(scope, base - 4, base - 4 + gap, within12);
    const net = retentionTotal(network, base, base + gap, within12);
    return {
      rate: cur.base ? cur.rate : null,
      prior: prior.base ? prior.rate : null,
      network: net.base ? net.rate : null,
      label,
    };
  };
  return {
    season: summarize(SEASON_KPI_BASE, 1, false, 'Summer 2026 → Fall 2026'),
    yoy: summarize(YOY_KPI_BASE, 4, true, 'Fall 2025 → any program within 12 months'),
  };
}

export function retentionTrend(scope: CohortScope, network: CohortScope, gap: number, within12: boolean): RetentionPoint[] {
  const pts: RetentionPoint[] = [];
  for (let b = 0; b + gap < COHORT_SEASONS.length; b++) {
    const s = retentionTotal(scope, b, b + gap, within12);
    const n = retentionTotal(network, b, b + gap, within12);
    pts.push({
      base: b,
      comp: b + gap,
      label: within12 ? `${COHORT_SEASONS[b].short} +12 mo` : `${COHORT_SEASONS[b].short} → ${COHORT_SEASONS[b + gap].short}`,
      scope: s.base ? s.rate : null,
      network: n.base ? n.rate : null,
    });
  }
  return pts;
}
